import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';
import { INDUSTRIES, INDUSTRY_ORDER, INDUSTRY_LABELS, PILLAR } from '../data/industries';

const IndustryPage = ({ slug }) => {
  const { open: openLead } = useLeadDialog();
  const ind = INDUSTRIES[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!ind) return null;
  const Icon = ind.icon;
  const label = INDUSTRY_LABELS[slug];
  const others = INDUSTRY_ORDER.filter((s) => s !== slug).map((s) => ({ slug: s, ...INDUSTRIES[s], label: INDUSTRY_LABELS[s] }));

  return (
    <div className="bg-black min-h-screen" data-testid={`industry-page-${slug}`}>
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 20%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Industries</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">{label}</span>
            </nav>

            <div className="mt-7 grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                  <Icon className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">Industry · {label}</span>
                </div>
                <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                  {ind.title[0]} <br />
                  <span className="italic font-light brand-gradient-text">{ind.title[1]}</span>
                </h1>
                <p className="mt-6 text-[17px] lg:text-[18px] text-neutral-300 leading-relaxed max-w-2xl">{ind.lede}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    onClick={() => openLead({ source: `industry-${slug}-hero`, title: `Talk to us about ${label}` })}
                    className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                    data-testid={`${slug}-talk-btn`}
                  >
                    Talk to a specialist <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="#pillars" className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center">
                    Explore the pillars
                  </a>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="grid gap-2">
                  {ind.stats.map((s, i) => (
                    <div key={i} className="rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-3">
                      <div className="text-[10.5px] uppercase tracking-[0.16em] text-blue-400">{s.k}</div>
                      <div className="mt-1 text-[15px] font-semibold text-white">{s.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="relative bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
            <div className="relative rounded-2xl overflow-hidden border border-neutral-800 aspect-[21/9] bg-neutral-950 group">
              <img src={ind.image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.01] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/30 to-transparent" />
            </div>
          </div>
        </section>

        {/* PILLARS — Observability / Self-Healing / AI & Agents applied to industry */}
        <section id="pillars" className="relative bg-neutral-950 border-b border-neutral-900 scroll-mt-24">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="max-w-3xl">
              <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Three pillars · one platform</p>
              <h2 className="mt-3 text-[30px] sm:text-[40px] lg:text-[50px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                How MetaxuOne works for <span className="italic font-light text-neutral-400">{label.toLowerCase()}.</span>
              </h2>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-4">
              {[
                { key: 'observability', data: ind.pillars.observability, route: '/platform/observability' },
                { key: 'selfHealing',   data: ind.pillars.selfHealing,   route: '/platform/self-healing'  },
                { key: 'agents',        data: ind.pillars.agents,        route: '/platform/ai-agents'     },
              ].map(({ key, data, route }) => {
                const P = PILLAR[key];
                return (
                  <div key={key} className="group rounded-2xl border border-neutral-800 bg-black p-7 hover:border-blue-500 hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 rounded-xl bg-white text-neutral-950 flex items-center justify-center">
                        <P.icon className="w-5 h-5" />
                      </div>
                      <Link to={route} className="text-[11.5px] uppercase tracking-[0.14em] text-neutral-500 hover:text-blue-300 inline-flex items-center gap-1">
                        Pillar <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                    <h3 className="mt-5 text-[20px] font-semibold text-white">{P.label}</h3>
                    <p className="mt-3 text-[14px] text-neutral-400 leading-relaxed">{data.body}</p>
                    <ul className="mt-5 space-y-2.5">
                      {data.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-[13px] text-neutral-200">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section className="relative bg-black border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="max-w-3xl">
              <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Where it shows up</p>
              <h2 className="mt-3 text-[30px] sm:text-[40px] lg:text-[50px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                Live use cases <span className="italic font-light text-neutral-400">in {label.toLowerCase()}.</span>
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-3 gap-4">
              {ind.useCases.map((u) => (
                <div key={u.title} className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6 hover:border-blue-500 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-300 flex items-center justify-center">
                    <u.icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="mt-5 text-[16.5px] font-semibold text-white">{u.title}</h3>
                  <p className="mt-2 text-[13.5px] text-neutral-400 leading-relaxed">{u.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CROSS-INDUSTRY NAV */}
        <section className="relative bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div className="max-w-2xl">
                <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">More industries</p>
                <h2 className="mt-3 text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  See the platform in <span className="italic font-light text-neutral-400">your sector.</span>
                </h2>
              </div>
              <Link to="/platform" className="text-[13.5px] text-neutral-300 hover:text-white inline-flex items-center gap-1.5">
                Back to platform <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/industries/${o.slug}`}
                  className="group rounded-2xl border border-neutral-800 bg-black p-5 hover:border-blue-500 hover:-translate-y-0.5 transition-all"
                  data-testid={`industry-nav-${o.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-xl bg-white text-neutral-950 flex items-center justify-center">
                      <o.icon className="w-5 h-5" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <h3 className="mt-4 text-[18px] font-semibold text-white">{o.label}</h3>
                  <p className="mt-1 text-[12.5px] text-neutral-500 leading-relaxed line-clamp-2">{o.lede.split('.').slice(0,1).join('.')}.</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="relative bg-black">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(50% 60% at 20% 30%, rgba(59,130,246,0.18), transparent), radial-gradient(50% 60% at 80% 70%, rgba(255,255,255,0.04), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
            <h2 className="text-[30px] sm:text-[40px] lg:text-[48px] leading-[1.05] tracking-[-0.02em] font-semibold text-white max-w-3xl mx-auto">
              Ready to talk about <span className="italic font-light text-neutral-400">{label}?</span>
            </h2>
            <p className="mt-5 text-[16px] text-neutral-300 max-w-2xl mx-auto">Bring your environment and your goals — we&rsquo;ll bring the senior people who&rsquo;ve solved this before in your sector.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => openLead({ source: `industry-${slug}-bottom`, title: `Talk to us about ${label}` })}
                className="h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                data-testid={`${slug}-bottom-talk-btn`}
              >
                Talk to a specialist <ArrowRight className="w-4 h-4" />
              </Button>
              <Link to="/platform" className="h-12 px-7 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center">
                Back to platform
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IndustryPage;
